import { HTMLAttributes } from 'react'
import { useMedia } from 'react-use'

import Button from '@/components/base/button-more'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Media from '@/components/base/media'
import MediaParallax from '@/components/base/media-parallax'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { MediaQuery } from '@/constants'

import { cn } from '@/lib/utils'
import { WPLink, WPMedia } from '@/types'

export type FullBannerProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    variant: 'text-left' | 'text-right'
    pretitle: string
    title: string
    media: WPMedia
    link: WPLink
  }>

const FullBanner = ({ variant, pretitle, title, media, link, ...props }: FullBannerProps) => {
  const isMD = useMedia(MediaQuery.MEDIUM, false)

  return (
    <Section {...props}>
      <Grid className="gap-y-[2rem]">
        <div
          className={cn(
            'relative col-span-2',
            'mx-margin md:mx-[1rem]',
            'md:col-[1/-1] md:row-[1/-1]'
          )}
        >
          {media ? (
            <MediaParallax
              className={cn(
                'relative overflow-hidden rounded-[0.5rem] bg-light',
                'aspect-[350/380] md:aspect-[1420/950] lg:aspect-[1420/750]',
                'after:absolute after:left-0 after:top-0 after:size-full after:bg-black/25'
              )}
            >
              <span className="flex size-full">
                <Media className="object-cover" {...media} />
              </span>
            </MediaParallax>
          ) : null}
        </div>

        <Container
          className={cn(
            'relative col-span-2',
            'md:col-[1/-1] md:row-[1/-1]',
            'md:py-[5rem] lg:py-[9rem]',
            'flex items-end'
          )}
        >
          <Grid>
            <div
              className={cn(
                'col-span-2 md:col-span-3 lg:col-span-4 xl:col-span-3',
                'grid gap-y-[3rem]',
                'md:text-white',
                {
                  'md:col-start-4 lg:col-start-9 xl:col-start-10': variant === 'text-right'
                }
              )}
            >
              {pretitle ? (
                <Typography variant="upper-small" component="h3" className="font-bold uppercase">
                  {pretitle}
                </Typography>
              ) : null}

              {title ? (
                <Typography variant="main-title-default" component="h2">
                  {title}
                </Typography>
              ) : null}

              {link && link.url ? (
                <div className="flex">
                  <Button
                    href={link?.url}
                    target={link?.target}
                    className={cn({
                      dark: isMD
                    })}
                  >
                    {link?.title}
                  </Button>
                </div>
              ) : null}
            </div>
          </Grid>
        </Container>
      </Grid>
    </Section>
  )
}

export default FullBanner
